import * as React from 'react';

import { Image } from '@codes/ui-kit/components/ui/image';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { CodesLogoImage } from '@codes/assets';

import { cn } from '../../utils/index';

const PageDialog = DialogPrimitive.Root;

const PageDialogTrigger = DialogPrimitive.Trigger;

const PageDialogPortal = DialogPrimitive.Portal;

const PageDialogClose = DialogPrimitive.Close;

const PageDialogOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(
            'fixed inset-0 z-[999] bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            className
        )}
        {...props}
    />
));
PageDialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const PageDialogContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
        showCloseButton?: boolean;
        preventDismiss?: boolean;
    }
>(({ className, children, preventDismiss = true, ...props }, ref) => (
    <PageDialogPortal>
        <PageDialogOverlay />
        <DialogPrimitive.Content
            ref={ref}
            className={cn(
                'bg-white dark:bg-[#252525] fixed inset-0 z-[999] overflow-auto flex flex-col items-center w-full ',
                className
            )}
            {...props}
            onInteractOutside={preventDismiss ? e => e.preventDefault() : props.onInteractOutside}
            onEscapeKeyDown={preventDismiss ? e => e.preventDefault() : props.onEscapeKeyDown}
            onCloseAutoFocus={event => {
                event.preventDefault();
                document.body.style.pointerEvents = '';
            }}
        >
            <div className="max-md:hidden absolute top-6 left-5 z-[999] h-[50px] px-4 py-3 rounded-full border-[0.6px] border-gray-200 shadow-custom flex items-center justify-center ">
                <Image className="h-[26px]" src={CodesLogoImage} />
            </div>
            {children}
        </DialogPrimitive.Content>
    </PageDialogPortal>
));
PageDialogContent.displayName = DialogPrimitive.Content.displayName;

const PageDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className="bg-white dark:bg-[#252525] shrink-0 h-[52px] sticky top-0 z-[1000]
    flex items-center justify-center relative
    after:content-[''] after:absolute after:bottom-0 after:left-6 after:right-6
    after:h-[1px] after:bg-gray-200"
    >
        <div className={cn('text-base font-medium', className)} {...props} />
    </div>
);
PageDialogHeader.displayName = 'PageDialogHeader';

const PageDialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className="relative sticky bottom-0 bg-white dark:bg-[#252525] z-20 pb-6 px-6">
        <div className="pointer-events-none absolute -top-10 left-0 right-0 h-10 bg-gradient-to-t from-white dark:from-[#252525] to-transparent z-10" />
        <div className={cn('', className)} {...props} />
    </div>
);
PageDialogFooter.displayName = 'PageDialogFooter';

const PageDialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn('text-gray-900 text-center font-medium text-[18px] break-keep', className)}
        {...props}
    />
));
PageDialogTitle.displayName = DialogPrimitive.Title.displayName;

const PageDialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description ref={ref} className={cn('text-sm text-sub-title', className)} {...props} />
));
PageDialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
    PageDialog,
    PageDialogPortal,
    PageDialogOverlay,
    PageDialogTrigger,
    PageDialogClose,
    PageDialogContent,
    PageDialogHeader,
    PageDialogFooter,
    PageDialogTitle,
    PageDialogDescription,
};
